import type { MerchantApiClient } from "../../types.js";
import type {
  MerchantPolicyActivationInput,
  MerchantPolicyHistoryFilters,
  MerchantPolicyHistoryList,
  MerchantPolicyPage,
  MerchantPolicySettings,
  MerchantPolicyValidation,
  PolicyOverrideDraft,
  PolicyPreviewResult,
  PolicyPreviewSample,
  PriceMatchPolicy,
  PriceMatchPolicyDto,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

export interface PoliciesApi {
  getPolicyPage(): Promise<MerchantPolicyPage>;
  listPolicyHistory(filters: MerchantPolicyHistoryFilters): Promise<MerchantPolicyHistoryList>;
  updatePolicySettings(
    input: MerchantPolicyActivationInput,
    idempotencyKey: string,
  ): Promise<MerchantPolicyPage>;
  validatePolicySettings(settings: MerchantPolicySettings): Promise<MerchantPolicyValidation>;
  previewPolicy(
    policy: PriceMatchPolicy,
    sample: PolicyPreviewSample,
  ): Promise<PolicyPreviewResult>;
  getProductPolicy(productId: string, variantId?: string): Promise<PriceMatchPolicyDto>;
  updateProductPolicy(
    draft: PolicyOverrideDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchPolicyDto>;
  deleteProductPolicy(
    productId: string,
    idempotencyKey: string,
    variantId?: string,
  ): Promise<PriceMatchPolicyDto>;
}

export function createPoliciesApi(client: MerchantApiClient): PoliciesApi {
  return {
    async getPolicyPage() {
      return unwrap(
        await client.get<DataEnvelope<MerchantPolicyPage>>("/v1/api/policies/price-match/page"),
      );
    },
    async listPolicyHistory(filters) {
      return unwrap(
        await client.get<DataEnvelope<MerchantPolicyHistoryList>>(
          `/v1/api/policies/price-match/history?${historyQuery(filters)}`,
        ),
      );
    },
    async updatePolicySettings(input, idempotencyKey) {
      return unwrap(
        await client.put<DataEnvelope<MerchantPolicyPage>>(
          "/v1/api/policies/price-match",
          {
            settings: input.settings,
            reason: input.reason,
            acknowledgedMarginRisk: input.acknowledgedMarginRisk,
            acknowledgedLegalReview: input.acknowledgedLegalReview,
          },
          mutationOptions(idempotencyKey),
        ),
      );
    },
    async validatePolicySettings(settings) {
      return unwrap(
        await client.post<DataEnvelope<MerchantPolicyValidation>>(
          "/v1/api/policies/price-match/validate",
          { settings },
        ),
      );
    },
    async previewPolicy(policy, sample) {
      return unwrap(
        await client.post<DataEnvelope<PolicyPreviewResult>>(
          "/v1/api/policies/price-match/preview",
          { policy, sample },
        ),
      );
    },
    async getProductPolicy(productId, variantId) {
      return unwrap(
        await client.get<DataEnvelope<PriceMatchPolicyDto>>(policyTargetUrl(productId, variantId)),
      );
    },
    async updateProductPolicy(draft, idempotencyKey) {
      return unwrap(
        await client.put<DataEnvelope<PriceMatchPolicyDto>>(
          policyTargetUrl(draft.scopeId, draft.variantId),
          {
            enabled: draft.enabled,
            policyOverride: draft.policyOverride,
          },
          mutationOptions(idempotencyKey),
        ),
      );
    },
    async deleteProductPolicy(productId, idempotencyKey, variantId) {
      return unwrap(
        await client.delete<DataEnvelope<PriceMatchPolicyDto>>(
          policyTargetUrl(productId, variantId),
          undefined,
          mutationOptions(idempotencyKey),
        ),
      );
    },
  };
}

function policyTargetUrl(productId: string, variantId?: string): string {
  const path = `/v1/api/products/${encodeURIComponent(productId)}/policy`;
  return variantId === undefined ? path : `${path}?variantId=${encodeURIComponent(variantId)}`;
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function historyQuery(filters: MerchantPolicyHistoryFilters): string {
  const query = new URLSearchParams();
  query.set("filter", filters.filter);
  query.set("limit", String(filters.limit));
  if (filters.cursor !== undefined && filters.cursor.length > 0) {
    query.set("cursor", filters.cursor);
  }
  if (filters.dateFrom !== undefined && filters.dateFrom.length > 0) {
    query.set("dateFrom", filters.dateFrom);
  }
  if (filters.dateTo !== undefined && filters.dateTo.length > 0) {
    query.set("dateTo", filters.dateTo);
  }
  return query.toString();
}

function mutationOptions(idempotencyKey: string) {
  return { idempotencyKey };
}
